import { rememberEntryPoint } from "@/lib/utm";

const EARLY_ACCESS_ENTRYPOINT_KEY = "rr_early_access_entrypoint";

export const EARLY_ACCESS_SECTION_ID = "beta";
export const EARLY_ACCESS_EMAIL_CONTAINER_ID = "founding-email";
export const EARLY_ACCESS_HASH = `#${EARLY_ACCESS_SECTION_ID}`;

function canUseDom() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function sanitizeEntryPoint(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_")
    .slice(0, 40);
}

function isEarlyAccessHash(hash: string) {
  return hash === EARLY_ACCESS_HASH || hash === `#${EARLY_ACCESS_EMAIL_CONTAINER_ID}`;
}

export function rememberEarlyAccessEntryPoint(entryPoint: string | undefined) {
  if (!canUseDom() || !entryPoint) return;
  const normalized = sanitizeEntryPoint(entryPoint);
  if (!normalized) return;

  rememberEntryPoint(normalized);

  try {
    window.sessionStorage.setItem(EARLY_ACCESS_ENTRYPOINT_KEY, normalized);
  } catch {
    // Ignore storage errors in private/incognito contexts.
  }
}

export function resolveEarlyAccessSource(source: string | undefined) {
  const base = source?.trim();
  if (!canUseDom()) return base;

  let entryPoint = "";
  try {
    entryPoint = window.sessionStorage.getItem(EARLY_ACCESS_ENTRYPOINT_KEY) ?? "";
  } catch {
    entryPoint = "";
  }

  const parts = [base, entryPoint].filter((part): part is string => Boolean(part));
  if (parts.length === 0) return undefined;

  return parts.join(":").slice(0, 80);
}

export function focusEarlyAccessEmailInput({ select = false } = {}) {
  if (!canUseDom()) return false;

  const emailInput = document.querySelector<HTMLInputElement>(
    `#${EARLY_ACCESS_EMAIL_CONTAINER_ID} input[name='email']`,
  );

  if (!emailInput) return false;

  emailInput.focus({ preventScroll: true });
  if (select) {
    emailInput.select();
  }
  return true;
}

export function scrollToEarlyAccess({ behavior = "smooth" as ScrollBehavior, select = true } = {}) {
  if (!canUseDom()) return false;

  const section = document.getElementById(EARLY_ACCESS_SECTION_ID);
  if (!section) return false;

  section.scrollIntoView({ behavior, block: "start" });

  window.setTimeout(() => {
    focusEarlyAccessEmailInput({ select });
  }, behavior === "smooth" ? 220 : 0);

  window.setTimeout(() => {
    focusEarlyAccessEmailInput({ select });
  }, 520);

  return true;
}

export function syncEarlyAccessFromHash() {
  if (!canUseDom()) return false;
  if (!isEarlyAccessHash(window.location.hash)) return false;

  scrollToEarlyAccess({ behavior: "auto", select: false });
  return true;
}

export function navigateToEarlyAccessOnPage({
  entryPoint,
  behavior = "smooth" as ScrollBehavior,
}: {
  entryPoint?: string;
  behavior?: ScrollBehavior;
}) {
  if (!canUseDom()) return false;

  rememberEarlyAccessEntryPoint(entryPoint);
  if (window.location.hash !== EARLY_ACCESS_HASH) {
    window.history.replaceState(null, "", EARLY_ACCESS_HASH);
  }
  return scrollToEarlyAccess({ behavior });
}
